import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { WithAuthenticationRequiredOptions } from '@rhg/libs/auth0';
import useAuth from '@rhg/libs/auth0/use-auth';
import { Box, Toolbar } from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import React, { ComponentType, FC, useEffect } from 'react';

/*
 * Slightly modified version of the HoC definition from @auth0/auth0-react
 * Modified the default for returnTo and onRedirecting variable definitions
 * Modified to useAuth() instead of useAuth0() to support e2e
 *
 * https://github.com/auth0/auth0-react/blob/1f460701ab9cef676a4748ca3cd2f9f38411f2d1/src/with-authentication-required.tsx
 */
const withAuthenticationRequired = <P extends object>(
    Component: ComponentType<P>,
    options: WithAuthenticationRequiredOptions = {},
    LoadingComponent?: FC
): FC<P> => {
    return function WithAuthenticationRequired(props: P): React.JSX.Element {
        const { isAuthenticated, isLoading, loginWithRedirect, error } =
            useAuth();
        const {
            returnTo = (): string =>
                `${window.location.pathname}${window.location.search}`,
            loginOptions,
        } = options;
        const { push } = useRouter();

        /**
         * The route is authenticated if the user has valid auth and there are no
         * JWT claim mismatches.
         */
        const routeIsAuthenticated = isAuthenticated;

        useEffect(() => {
            if (isLoading || routeIsAuthenticated) {
                return;
            }
            if (error) {
                push(routes.error({ title: error.name, body: error.message }));
                return;
            }

            const opts = {
                ...loginOptions,
                appState: {
                    ...(loginOptions && loginOptions.appState),
                    returnTo:
                        typeof returnTo === 'function' ? returnTo() : returnTo,
                },
            };
            (async (): Promise<void> => {
                await loginWithRedirect(opts);
            })();
        }, [
            isLoading,
            routeIsAuthenticated,
            loginWithRedirect,
            loginOptions,
            returnTo,
            error,
            push,
        ]);

        return routeIsAuthenticated ? (
            <Component {...props} />
        ) : (
            <Box sx={{ position: 'fixed', left: 0, top: 0 }}>
                {LoadingComponent ? (
                    <LoadingComponent />
                ) : (
                    <>
                        <Toolbar />
                        <FullWidthLinearProgress />
                    </>
                )}
            </Box>
        );
    };
};

/**
 * This is a wrapper on top of withAuthenticationRequired
 * that tells Auth0 to use the wrapped page as the
 * loading state, preserving SSR for the wrapped page.
 */
const withAuth = (
    WrappedComponent: FC,
    LoadingComponent?: FC,
    options: WithAuthenticationRequiredOptions = {}
) => {
    const AuthenticatedState = () => <WrappedComponent />;
    return withAuthenticationRequired(
        AuthenticatedState,
        options,
        LoadingComponent
    );
};

export default withAuth;
